import { CommonModule } from '@angular/common'
import {
  Component,
  Input,
  ChangeDetectionStrategy,
  HostBinding,
} from '@angular/core'
import { RouterModule } from '@angular/router'
import {
  DisplayType,
  TypographyComponent,
  TypographySize,
  TypographyVariant,
} from '../typography/typography.component'

export type ColorVariant = 'primary' | 'secondary'
export type LinkTarget = '_self' | '_blank' | '_parent' | '_top'

@Component({
  selector: 'hyperkit-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  standalone: true,
  imports: [CommonModule, TypographyComponent, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkComponent {
  @Input() href = ''
  @Input() routerLink?: string[] | string
  @Input() target: LinkTarget = '_self'
  @Input() active = false
  @Input() variant: TypographyVariant = 'h1'
  @Input() size: TypographySize = 'sm'
  @Input() color: ColorVariant = 'primary'
  @Input() underline: 'always' | 'hover' | 'none' = 'hover'
  @Input() rel: 'noopener' | 'noreferrer' | 'none' = 'noopener'
  @Input() ariaLabel = ''
  @Input() display: DisplayType = 'block'
  @Input() text = ''

  /** Determine if the component should render as a clickable link */
  get isLink(): boolean {
    return !!this.routerLink || !!this.href
  }

  /** Compute href from routerLink or href */
  get computedHref(): string | null {
    if (this.routerLink) {
      if (typeof this.routerLink === 'string') {
        return this.routerLink.startsWith('/')
          ? this.routerLink
          : `/${this.routerLink}`
      }
      return (
        '/' + this.routerLink.map((p) => `${p}`.replace(/^\/+/, '')).join('/')
      )
    }
    return this.href || null
  }

  /** Compute rel attribute for external links */
  get hostRel(): string {
    return this.target === '_blank' ? this.rel : ''
  }

  @HostBinding('class') get hostClasses(): string {
    const classes = [this.display, this.active ? 'active' : '']
    return classes.filter(Boolean).join(' ')
  }
}
